import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BudgetForms.css';

const BudgetForms = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePeriod, setActivePeriod] = useState(1);
  const [activeType, setActiveType] = useState('project'); // 'project' or 'subaward'
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');

  const fileInputRef = useRef(null);
  
  // Fetch application details and budget data
  useEffect(() => {
    const fetchApplicationData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch application details
        const appResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!appResponse.ok) {
          throw new Error('Failed to fetch application details');
        }
        
        const appData = await appResponse.json();
        setApplication(appData.application);
        
        // Fetch budget data if it exists
        const budgetResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/budget`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (budgetResponse.ok) {
          const budgetResult = await budgetResponse.json();
          console.log('Budget fetch result:', budgetResult);
          
          // Check if this is Excel-based budget
          if (budgetResult.budgetType === 'excel') {
            console.log('Setting Excel budget data:', budgetResult.fileName, budgetResult.fileUrl);
            setUploadedFileName(budgetResult.fileName || '');
            setUploadedFileUrl(budgetResult.fileUrl || '');
          } else {
            // Legacy form-based budget data
            setBudgetData(budgetResult.budgetData);
            
            // If we have budget data, set active period and type to the first available
            if (budgetResult.budgetData && Object.keys(budgetResult.budgetData).length > 0) {
              const periods = Object.keys(budgetResult.budgetData);
              if (periods.length > 0) {
                setActivePeriod(parseInt(periods[0]));
                
                const periodData = budgetResult.budgetData[periods[0]];
                if (periodData && Object.keys(periodData).length > 0) {
                  setActiveType(Object.keys(periodData)[0]);
                }
              }
            }
          }
        } else {
          console.log('No budget data found (404), initializing empty state');
          // Initialize with empty budget data structure for backward compatibility
          const emptyBudgetData = {
            1: {
              project: getEmptyBudgetForm(),
              subaward: getEmptyBudgetForm()
            }
          };
          setBudgetData(emptyBudgetData);
          
          // Initialize Excel upload state as empty
          setUploadedFileName('');
          setUploadedFileUrl('');
        }
      } catch (error) {
        console.error('Error fetching application data:', error);
        setError('Failed to load application data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchApplicationData();
  }, [applicationId]);

  const recalculateCategoryTotals = (form, category) => {
    if (!form || !form[category]) return;
  
    if (category === 'seniorKeyPersonnel' || category === 'equipment') {
      const items = form[category].items || [];
      form[category].subtotal = items.reduce((sum, item) => {
        const funds = parseFloat(item.fundsRequested || 0);
        return sum + (isNaN(funds) ? 0 : funds);
      }, 0);
    }
  
    if (category === 'otherPersonnel') {
      const items = form[category].items || [];
      form[category].subtotal = items.reduce((sum, item) => {
        const funds = parseFloat(item.fundsRequested || 0);
        return sum + (isNaN(funds) ? 0 : funds);
      }, 0);
      form[category].totalCount = items.reduce((sum, item) => {
        const count = parseInt(item.numberOfPersonnel || 0);
        return sum + (isNaN(count) ? 0 : count);
      }, 0);
    }
  
    if (category === 'otherDirectCosts') {
      const fields = form[category].fields || [];
      const items = form[category].items || [];
      form[category].subtotal =
        fields.reduce((sum, f) => sum + (parseFloat(f.value) || 0), 0) +
        items.reduce((sum, f) => sum + (parseFloat(f.value) || 0), 0);
    }

    if (category === 'indirectCosts') {
      const items = form[category].items || [];
      form[category].subtotal = items.reduce((sum, item) => {
        const funds = parseFloat(item.fundsRequested || 0);
        return sum + (isNaN(funds) ? 0 : funds);
      }, 0);
    }
  };
  
  // Initialize an empty budget form with all required fields
  const getEmptyBudgetForm = () => {
    return {
      uei: '', // UEI Number (required)
      organization: '', // Organization name
      periodStartDate: '', // Start date (required)
      periodEndDate: '', // End date (required)
      
      // Section A: Senior/Key Person
      seniorKeyPersonnel: {
        items: [], // Will contain senior/key personnel entries
        subtotal: 0, // Total funds requested for all personnel
        additionalFileUrl: '', // URL for uploaded additional senior/key person document
        additionalFileName: '' // Filename of the uploaded document
      },
      otherPersonnel: {
        items: [
          // no default personnel types anymore
        ],
        subtotal: 0,
        totalCount: 0
      },
      equipment: {
        items: [],
        subtotal: 0
      },
      travel: {
        domestic: '',
        foreign: ''
      },
      participantSupport: {
        tuition: '',
        stipends: '',
        travel: '',
        subsistence: '',
        other: '',
        numberOfParticipants: ''
      },
      otherDirectCosts: {
        fields: [
          { label: 'Materials and Supplies', value: '' },
          { label: 'Publication Costs', value: '' },
          { label: 'Consultant Services', value: '' },
          { label: 'ADP/Computer Services', value: '' },
          { label: 'Subawards/Consortium/Contractual Costs', value: '' },
          { label: 'Equipment or Facility Rental/User Fees', value: '' },
          { label: 'Alterations and Renovations', value: '' }
        ],
        items: [],
        subtotal: 0
      },
      indirectCosts: {
        items: [
          {
            type: 'F&A',
            rate: '',
            base: '',
            fundsRequested: ''
          }
        ],
        subtotal: 0,
        cognizantAgency: '',
        cognizantPOC: '',
        cognizantPhone: ''
      },
      fee: '',
      // Totals across sections
      totalSalaryWagesFringes: 0
    };
  };
  
  // Get current budget form based on active period and type
  const getCurrentBudgetForm = () => {
    if (!budgetData || !budgetData[activePeriod] || !budgetData[activePeriod][activeType]) {
      // Return empty form structure without triggering state update during render
      return getEmptyBudgetForm();
    }
    
    return budgetData[activePeriod][activeType];
  };
  
  // Handle creating a new period
  const handleAddPeriod = () => {
    const newPeriod = Math.max(...Object.keys(budgetData).map(Number)) + 1;
    const updatedBudgetData = {
      ...budgetData,
      [newPeriod]: {
        project: getEmptyBudgetForm(),
        subaward: getEmptyBudgetForm()
      }
    };
    
    setBudgetData(updatedBudgetData);
    setActivePeriod(newPeriod);
    setActiveType('project');
  };
  
  // Handle saving budget data
  const handleSaveBudget = async () => {
    try {
      setAttemptedSubmit(true);

      // For Excel upload, just check if file is uploaded
      if (!uploadedFileUrl) {
        setError('Please upload a budget file before saving.');
        return;
      }
      
      setIsSaving(true);
      setSaveSuccess(false);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/budget`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          budgetType: 'excel',
          fileName: uploadedFileName,
          fileUrl: uploadedFileUrl,
          uploadDate: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save budget data');
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      setAttemptedSubmit(false);
      
      console.log('Budget saved successfully. Current state:', {
        uploadedFileName,
        uploadedFileUrl
      });
      
      // Re-fetch budget data to ensure display is updated
      const budgetResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/budget`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (budgetResponse.ok) {
        const budgetResult = await budgetResponse.json();
        console.log('Re-fetched budget after save:', budgetResult);
        
        if (budgetResult.budgetType === 'excel') {
          setUploadedFileName(budgetResult.fileName || '');
          setUploadedFileUrl(budgetResult.fileUrl || '');
        }
      }
    } catch (error) {
      console.error('Error saving budget data:', error);
      setError('Failed to save budget data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle going back to grant applications
  const handleReturn = () => {
    navigate('/grant-submission');
  };

  // Handle updating fields in the form
  const handleInputChange = (category, idx, field, value) => {
    const currentForm = getCurrentBudgetForm();
    const newBudgetData = { ...budgetData };
    
    if (!newBudgetData[activePeriod]) {
      newBudgetData[activePeriod] = {};
    }
    
    if (!newBudgetData[activePeriod][activeType]) {
      newBudgetData[activePeriod][activeType] = getEmptyBudgetForm();
    }
    
    if (category === 'basic') {
      // Handle basic information fields
      newBudgetData[activePeriod][activeType][field] = value;
    } else if (category === 'seniorKeyPersonnel') {
      if (idx === undefined) {
        // For additional file URLs or other non-item properties
        newBudgetData[activePeriod][activeType].seniorKeyPersonnel[field] = value;
      } else {
        // For individual personnel items
        const items = [...newBudgetData[activePeriod][activeType].seniorKeyPersonnel.items];
        if (!items[idx]) {
          items[idx] = {};
        }
        
        items[idx] = { ...items[idx], [field]: value };
        
        // If this is the fundsRequested field, recalculate the subtotal
        if (field === 'fundsRequested') {
          const subtotal = items.reduce((sum, item) => {
            const funds = parseFloat(item.fundsRequested || 0);
            return sum + (isNaN(funds) ? 0 : funds);
          }, 0);
          
          newBudgetData[activePeriod][activeType].seniorKeyPersonnel.subtotal = subtotal;
          
          // Update total across sections
          updateTotalSalaryWagesFringes(newBudgetData[activePeriod][activeType]);
        }
        
        newBudgetData[activePeriod][activeType].seniorKeyPersonnel.items = items;
      }
    } else if (category === 'otherPersonnel') {
      // For other personnel items
      const items = [...newBudgetData[activePeriod][activeType].otherPersonnel.items];
      if (!items[idx]) {
        items[idx] = {};
      }
      
      items[idx] = { ...items[idx], [field]: value };
      
      // If this is a field that affects calculations, recalculate
      if (['fundsRequested', 'numberOfPersonnel'].includes(field)) {
        // Recalculate subtotal
        const subtotal = items.reduce((sum, item) => {
          const funds = parseFloat(item.fundsRequested || 0);
          return sum + (isNaN(funds) ? 0 : funds);
        }, 0);
        
        // Recalculate total personnel count
        const totalCount = items.reduce((sum, item) => {
          const count = parseInt(item.numberOfPersonnel || 0);
          return sum + (isNaN(count) ? 0 : count);
        }, 0);
        
        newBudgetData[activePeriod][activeType].otherPersonnel.subtotal = subtotal;
        newBudgetData[activePeriod][activeType].otherPersonnel.totalCount = totalCount;
        
        // Update total across sections
        updateTotalSalaryWagesFringes(newBudgetData[activePeriod][activeType]);
      }
      
      newBudgetData[activePeriod][activeType].otherPersonnel.items = items;
    } else if (category === 'equipment') {
      const items = [...newBudgetData[activePeriod][activeType].equipment.items];
      if (!items[idx]) items[idx] = {};
      items[idx] = { ...items[idx], [field]: value };
  
      // Recalculate subtotal
      const subtotal = items.reduce((sum, item) => {
        const funds = parseFloat(item.fundsRequested || 0);
        return sum + (isNaN(funds) ? 0 : funds);
      }, 0);
      newBudgetData[activePeriod][activeType].equipment.subtotal = subtotal;
      newBudgetData[activePeriod][activeType].equipment.items = items;
    } else if (category === 'travel') {
      newBudgetData[activePeriod][activeType].travel[field] = value;
    } else if (category === 'participantSupport') {
      newBudgetData[activePeriod][activeType].participantSupport[field] = value;
    } else if (category === 'otherDirectCosts') {
      if (field === 'additional') {
        // Update additional field value
        const additionalFields = [...newBudgetData[activePeriod][activeType].otherDirectCosts.items];
        additionalFields[idx].value = value;
        newBudgetData[activePeriod][activeType].otherDirectCosts.items = additionalFields;
      } else {
        // Update fixed field value
        const fields = [...newBudgetData[activePeriod][activeType].otherDirectCosts.fields];
        fields[idx].value = value;
        newBudgetData[activePeriod][activeType].otherDirectCosts.fields = fields;
      }
      // Recalculate subtotal
      const subtotal =
        newBudgetData[activePeriod][activeType].otherDirectCosts.fields.reduce(
          (sum, f) => sum + (parseFloat(f.value) || 0),
          0
        ) +
        newBudgetData[activePeriod][activeType].otherDirectCosts.items.reduce(
          (sum, f) => sum + (parseFloat(f.value) || 0),
          0
        );
      newBudgetData[activePeriod][activeType].otherDirectCosts.subtotal = subtotal;
    } else if (category === 'indirectCosts') {
      if (['cognizantAgency', 'cognizantPOC', 'cognizantPhone'].includes(field)) {
        newBudgetData[activePeriod][activeType].indirectCosts[field] = value;
      } else {
        // Update item
        const items = [...newBudgetData[activePeriod][activeType].indirectCosts.items];
        if (!items[idx]) items[idx] = {};
        items[idx] = { ...items[idx], [field]: value };
  
        // Recalculate subtotal
        const subtotal = items.reduce((sum, item) => {
          const funds = parseFloat(item.fundsRequested || 0);
          return sum + (isNaN(funds) ? 0 : funds);
        }, 0);
        newBudgetData[activePeriod][activeType].indirectCosts.subtotal = subtotal;
        newBudgetData[activePeriod][activeType].indirectCosts.items = items;
      }
    } else if (category === 'fee') {
      newBudgetData[activePeriod][activeType].fee = value;
    }
    
    setBudgetData(newBudgetData);
  };

  // Helper function to update totals across sections
  const updateTotalSalaryWagesFringes = (formData) => {
    const sectionATotal = formData.seniorKeyPersonnel?.subtotal || 0;
    const sectionBTotal = formData.otherPersonnel?.subtotal || 0;
    
    formData.totalSalaryWagesFringes = sectionATotal + sectionBTotal;
  };

  const addIndirectCostItem = () => {
    const newBudgetData = { ...budgetData };
    const items = [
      ...newBudgetData[activePeriod][activeType].indirectCosts.items,
      { type: '', rate: '', base: '', fundsRequested: '' }
    ];
    newBudgetData[activePeriod][activeType].indirectCosts.items = items;
    setBudgetData(newBudgetData);
  };

  const renderIndirectCostsSection = () => {
    const currentForm = getCurrentBudgetForm();
    const ic = currentForm.indirectCosts || { items: [], subtotal: 0, cognizantAgency: '', cognizantPOC: '', cognizantPhone: '' };
  
    return (
      <div className="budget-section">
        <h3>Section H: Indirect Costs</h3>
        {/* <div className="form-row">
          <div className="form-group">
            <label>Cognizant Federal Agency Name:</label>
            <input
              type="text"
              value={ic.cognizantAgency}
              onChange={e => handleInputChange('indirectCosts', undefined, 'cognizantAgency', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>POC Name:</label>
            <input
              type="text"
              value={ic.cognizantPOC}
              onChange={e => handleInputChange('indirectCosts', undefined, 'cognizantPOC', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>POC Phone Number:</label>
            <input
              type="text"
              value={ic.cognizantPhone}
              onChange={e => handleInputChange('indirectCosts', undefined, 'cognizantPhone', e.target.value)}
            />
          </div>
        </div> */}
        {ic.items.map((item, idx) => (
          <div className="form-row" key={idx}>
            <div className="form-group required">
              <label>Indirect Cost Type:</label>
              <input
                type="text"
                value={item.type}
                onChange={e => handleInputChange('indirectCosts', idx, 'type', e.target.value)}
                required
              />
            </div>
            <div className="form-group required">
              <label>Indirect Cost Rate (%):</label>
              <input
                type="number"
                value={item.rate}
                onChange={e => handleInputChange('indirectCosts', idx, 'rate', e.target.value)}
                step="0.01"
                min="0"
                required
              />
            </div>
            <div className="form-group required">
              <label>Indirect Cost Base ($):</label>
              <input
                type="number"
                value={item.base}
                onChange={e => handleInputChange('indirectCosts', idx, 'base', e.target.value)}
                step="0.01"
                min="0"
                required
              />
            </div>
            <div className="form-group required">
              <label>Funds Requested ($):</label>
              <input
                type="number"
                value={item.fundsRequested}
                onChange={e => handleInputChange('indirectCosts', idx, 'fundsRequested', e.target.value)}
                step="0.01"
                min="0"
                required
              />
            </div>
            <button
              type="button"
              className="remove-item-btn"
              onClick={() => removeItemFromCategory('indirectCosts', idx)}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="add-item-btn"
          onClick={addIndirectCostItem}
        >
          + Add Indirect Cost
        </button>
        <div className="subtotal">
          <span>Total Indirect Costs:</span>
          <span>${ic.subtotal.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const renderTotalDirectAndIndirectSection = () => {
    const currentForm = getCurrentBudgetForm();
    // Section G
    const sectionA = currentForm.seniorKeyPersonnel?.subtotal || 0;
    const sectionB = currentForm.otherPersonnel?.subtotal || 0;
    const sectionC = currentForm.equipment?.subtotal || 0;
    const travel = currentForm.travel || {};
    const sectionD = (parseFloat(travel.domestic || 0) || 0) + (parseFloat(travel.foreign || 0) || 0);
    const ps = currentForm.participantSupport || {};
    const sectionE = ['tuition', 'stipends', 'travel', 'subsistence', 'other']
      .map(f => parseFloat(ps[f] || 0) || 0)
      .reduce((sum, v) => sum + v, 0);
    const odc = currentForm.otherDirectCosts || {};
    const sectionF = odc.subtotal || 0;
    const sectionG = sectionA + sectionB + sectionC + sectionD + sectionE + sectionF;
    // Section H
    const ic = currentForm.indirectCosts || {};
    const sectionH = ic.subtotal || 0;
    const total = sectionG + sectionH;
  
    return (
      <div className="budget-section total-section">
        <h3>Section I: Total Direct and Indirect Costs</h3>
        <div className="total-summary">
          <div className="total-row">
            <span>Total Direct and Indirect Costs (Sections G & H):</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFeeSection = () => {
    const currentForm = getCurrentBudgetForm();
    return (
      <div className="budget-section">
        <h3>Section J: Fee</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Fee ($):</label>
            <input
              type="number"
              value={currentForm.fee || ''}
              onChange={e => handleInputChange('fee', undefined, 'fee', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderTotalCostsAndFeeSection = () => {
    const currentForm = getCurrentBudgetForm();
    // Section I
    const sectionA = currentForm.seniorKeyPersonnel?.subtotal || 0;
    const sectionB = currentForm.otherPersonnel?.subtotal || 0;
    const sectionC = currentForm.equipment?.subtotal || 0;
    const travel = currentForm.travel || {};
    const sectionD = (parseFloat(travel.domestic || 0) || 0) + (parseFloat(travel.foreign || 0) || 0);
    const ps = currentForm.participantSupport || {};
    const sectionE = ['tuition', 'stipends', 'travel', 'subsistence', 'other']
      .map(f => parseFloat(ps[f] || 0) || 0)
      .reduce((sum, v) => sum + v, 0);
    const odc = currentForm.otherDirectCosts || {};
    const sectionF = odc.subtotal || 0;
    const sectionG = sectionA + sectionB + sectionC + sectionD + sectionE + sectionF;
    const ic = currentForm.indirectCosts || {};
    const sectionH = ic.subtotal || 0;
    const sectionI = sectionG + sectionH;
    const fee = parseFloat(currentForm.fee || 0) || 0;
    const total = sectionI + fee;
  
    return (
      <div className="budget-section total-section">
        <h3>Section K: Total Costs and Fee</h3>
        <div className="total-summary">
          <div className="total-row">
            <span>Total Costs and Fee (Sections I & J):</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };
  



  const addOtherDirectCostField = () => {
    const newBudgetData = { ...budgetData };
    const additionalFields = [
      ...newBudgetData[activePeriod][activeType].otherDirectCosts.items,
      { label: '', value: '' }
    ];
    newBudgetData[activePeriod][activeType].otherDirectCosts.items = additionalFields;
    setBudgetData(newBudgetData);
  };

  const renderDirectCostsSection = () => {
    const currentForm = getCurrentBudgetForm();
    // Section A
    const sectionA = currentForm.seniorKeyPersonnel?.subtotal || 0;
    // Section B
    const sectionB = currentForm.otherPersonnel?.subtotal || 0;
    // Section C
    const sectionC = currentForm.equipment?.subtotal || 0;
    // Section D
    const travel = currentForm.travel || {};
    const sectionD = (parseFloat(travel.domestic || 0) || 0) + (parseFloat(travel.foreign || 0) || 0);
    // Section E
    const ps = currentForm.participantSupport || {};
    const sectionE = ['tuition', 'stipends', 'travel', 'subsistence', 'other']
      .map(f => parseFloat(ps[f] || 0) || 0)
      .reduce((sum, v) => sum + v, 0);
    // Section F
    const odc = currentForm.otherDirectCosts || {};
    const sectionF = odc.subtotal || 0;
  
    const totalDirectCosts = sectionA + sectionB + sectionC + sectionD + sectionE + sectionF;
  
    return (
      <div className="budget-section total-section">
        <h3>Section G: Direct Costs</h3>
        <div className="total-summary">
          <div className="total-row">
            <span>Total Direct Costs (Sections A-F):</span>
            <span>${totalDirectCosts.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderOtherDirectCostsSection = () => {
    const currentForm = getCurrentBudgetForm();
    const odc = currentForm.otherDirectCosts || { fields: [], items: [], subtotal: 0 };
  
    return (
      <div className="budget-section">
        <h3>Section F: Other Direct Costs</h3>
        {odc.fields.map((field, idx) => (
          <div className="form-row" key={`odc-field-${idx}`}>
            <div className="form-group required">
              <label>{field.label} ($):</label>
              <input
                type="number"
                value={field.value}
                onChange={e => handleInputChange('otherDirectCosts', idx, 'fixed', e.target.value)}
                step="0.01"
                min="0"
              />
            </div>
          </div>
        ))}
        {odc.items.map((field, idx) => (
          <div className="form-row" key={`odc-item-${idx}`}>
            <div className="form-group">
              <input
                type="text"
                placeholder="Other Cost Description"
                value={field.label}
                onChange={e => {
                  const newBudgetData = { ...budgetData };
                  newBudgetData[activePeriod][activeType].otherDirectCosts.items[idx].label = e.target.value;
                  setBudgetData(newBudgetData);
                }}
                style={{ width: 220, marginRight: 8 }}
              />
              <input
                type="number"
                placeholder="Funds Requested"
                value={field.value}
                onChange={e => handleInputChange('otherDirectCosts', idx, 'additional', e.target.value)}
                step="0.01"
                min="0"
                style={{ width: 140, marginRight: 8 }}
              />
              <button
                type="button"
                className="remove-item-btn"
                onClick={() => removeItemFromCategory('otherDirectCosts', idx)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="add-item-btn"
          onClick={addOtherDirectCostField}
        >
          + Add Other Direct Cost
        </button>
        <div className="subtotal">
          <span>Total Other Direct Costs:</span>
          <span>${odc.subtotal.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const renderParticipantSupportSection = () => {
    const currentForm = getCurrentBudgetForm();
    const ps = currentForm.participantSupport || {};
    const tuition = parseFloat(ps.tuition || 0);
    const stipends = parseFloat(ps.stipends || 0);
    const travel = parseFloat(ps.travel || 0);
    const subsistence = parseFloat(ps.subsistence || 0);
    const other = parseFloat(ps.other || 0);
    const total = [tuition, stipends, travel, subsistence, other]
      .reduce((sum, v) => sum + (isNaN(v) ? 0 : v), 0);
  
    return (
      <div className="budget-section">
        <h3>Section E: Participant/Trainee Support Costs</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="participant-number">Number of Participants/Trainees:</label>
            <input
              type="number"
              id="participant-number"
              value={ps.numberOfParticipants}
              onChange={e => handleInputChange('participantSupport', undefined, 'numberOfParticipants', e.target.value)}
              min="0"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="participant-tuition">Tuition/Fees/Insurance ($):</label>
            <input
              type="number"
              id="participant-tuition"
              value={ps.tuition}
              onChange={e => handleInputChange('participantSupport', undefined, 'tuition', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="participant-stipends">Stipends ($):</label>
            <input
              type="number"
              id="participant-stipends"
              value={ps.stipends}
              onChange={e => handleInputChange('participantSupport', undefined, 'stipends', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="participant-travel">Travel ($):</label>
            <input
              type="number"
              id="participant-travel"
              value={ps.travel}
              onChange={e => handleInputChange('participantSupport', undefined, 'travel', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="participant-subsistence">Subsistence ($):</label>
            <input
              type="number"
              id="participant-subsistence"
              value={ps.subsistence}
              onChange={e => handleInputChange('participantSupport', undefined, 'subsistence', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="participant-other">Other ($):</label>
            <input
              type="number"
              id="participant-other"
              value={ps.other}
              onChange={e => handleInputChange('participantSupport', undefined, 'other', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
        </div>
        <div className="subtotal">
          <span>Total Participant/Trainee Support Costs:</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const renderTravelSection = () => {
    const currentForm = getCurrentBudgetForm();
    const travel = currentForm.travel || { domestic: '', foreign: '' };
    const domestic = parseFloat(travel.domestic || 0);
    const foreign = parseFloat(travel.foreign || 0);
    const total = (isNaN(domestic) ? 0 : domestic) + (isNaN(foreign) ? 0 : foreign);
  
    return (
      <div className="budget-section">
        <h3>Section D: Travel</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="travel-domestic">Domestic Travel Costs ($):</label>
            <input
              type="number"
              id="travel-domestic"
              value={travel.domestic}
              onChange={e => handleInputChange('travel', undefined, 'domestic', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="travel-foreign">Foreign Travel Costs ($):</label>
            <input
              type="number"
              id="travel-foreign"
              value={travel.foreign}
              onChange={e => handleInputChange('travel', undefined, 'foreign', e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
        </div>
        <div className="subtotal">
          <span>Total Travel Costs:</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>
    );
  };


  
  
  // Add a new item to a category
  const addItemToCategory = (category, defaultItem = {}) => {
    const currentForm = getCurrentBudgetForm();
    const newBudgetData = { ...budgetData };
    
    if (!newBudgetData[activePeriod][activeType][category]) {
      newBudgetData[activePeriod][activeType][category] = { items: [] };
    }
    
    const items = [...newBudgetData[activePeriod][activeType][category].items];
    items.push(defaultItem);
    
    newBudgetData[activePeriod][activeType][category].items = items;
    setBudgetData(newBudgetData);
  };

  // Remove an item from a category
  const removeItemFromCategory = (category, idx) => {
    console.log("Removing item from category:", category, "at index:", idx);
    const currentForm = getCurrentBudgetForm();
    const newBudgetData = { ...budgetData };
    
    if (!newBudgetData[activePeriod][activeType][category] || 
        !newBudgetData[activePeriod][activeType][category].items) {
      return;
    }
    
    const items = [...newBudgetData[activePeriod][activeType][category].items];
    
    if (idx >= 0 && idx < items.length) {
      items.splice(idx, 1);
      newBudgetData[activePeriod][activeType][category].items = items;
      recalculateCategoryTotals(newBudgetData[activePeriod][activeType], category);
      setBudgetData(newBudgetData);
    }
  };

  // Render the Basic Information section (new)
  const renderBasicInfoSection = () => {
    const currentForm = getCurrentBudgetForm();
    
    return (
      <div className="budget-section basic-info-section">
        <h3>Budget Information</h3>
        
        <div className="form-row">
          <div className="form-group required">
            <label htmlFor="uei">UEI Number:</label>
            <input 
              type="text"
              id="uei"
              value={currentForm.uei || ''}
              onChange={(e) => handleInputChange('basic', undefined, 'uei', e.target.value)}
              className={!currentForm.uei ? 'field-error' : ''}
              required
            />
            {attemptedSubmit && !currentForm.uei && <div className="error-message">UEI Number is required</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="organization">Organization:</label>
            <input 
              type="text"
              id="organization"
              value={currentForm.organization || ''}
              onChange={(e) => handleInputChange('basic', undefined, 'organization', e.target.value)}
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group required">
            <label htmlFor="period-start-date">Start Date:</label>
            <input 
              type="date"
              id="period-start-date"
              value={currentForm.periodStartDate || ''}
              onChange={(e) => handleInputChange('basic', undefined, 'periodStartDate', e.target.value)}
              className={!currentForm.periodStartDate ? 'field-error' : ''}
              required
            />
            {attemptedSubmit && !currentForm.periodStartDate && <div className="error-message">Start Date is required</div>}
          </div>
          
          <div className="form-group required">
            <label htmlFor="period-end-date">End Date:</label>
            <input 
              type="date"
              id="period-end-date"
              value={currentForm.periodEndDate || ''}
              onChange={(e) => handleInputChange('basic', undefined, 'periodEndDate', e.target.value)}
              className={!currentForm.periodEndDate ? 'field-error' : ''}
              required
            />
            {attemptedSubmit && !currentForm.periodEndDate && <div className="error-message">End Date is required</div>}
          </div>
        </div>
      </div>
    );
  };

  // Render the Section A: Senior/Key Person section
  const renderSeniorKeyPersonnelSection = () => {
    const currentForm = getCurrentBudgetForm();
    const seniorKeyPersonnel = currentForm.seniorKeyPersonnel;

    console.log("Current Senior/Key Personnel Data:", seniorKeyPersonnel);
    
    // File input change handler
    const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      try {
        setIsSaving(true);
        
        // Get upload URL
        const getUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            documentId: 'additional-key-personnel',
            filename: file.name,
            contentType: file.type,
          })
        });

        if (!getUrlResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const urlData = await getUrlResponse.json();
        
        // Upload file to Cloud Storage
        const uploadResponse = await fetch(urlData.uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type
          },
          body: file
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        // Confirm upload
        const confirmResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            documentId: 'additional-key-personnel',
            filename: file.name,
            destination: urlData.destination,
            downloadUrl: urlData.downloadUrl
          })
        });

        if (!confirmResponse.ok) {
          throw new Error('Failed to confirm file upload');
        }
        
        // Update state with file URL and name
        handleInputChange('seniorKeyPersonnel', undefined, 'additionalFileUrl', urlData.downloadUrl);
        handleInputChange('seniorKeyPersonnel', undefined, 'additionalFileName', file.name);
        
      } catch (error) {
        console.error('Error uploading file:', error);
        setError('Failed to upload file: ' + error.message);
      } finally {
        setIsSaving(false);
      }
    };
    
    return (
      <div className="budget-section">
        <h3>Section A: Senior/Key Person</h3>
        
        {seniorKeyPersonnel.items.map((person, idx) => (
          <div key={idx} className="budget-item">
            <div className="budget-item-header">
              <h4>Senior/Key Person {idx + 1}</h4>
              <button 
                type="button" 
                className="remove-item-btn"
                onClick={() => removeItemFromCategory('seniorKeyPersonnel', idx)}
              >
                Remove
              </button>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`person-${idx}-prefix`}>Prefix:</label>
                <input 
                  type="text"
                  id={`person-${idx}-prefix`}
                  value={person.prefix || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'prefix', e.target.value)}
                />
              </div>
              
              <div className="form-group required">
                <label htmlFor={`person-${idx}-firstname`}>First Name:</label>
                <input 
                  type="text"
                  id={`person-${idx}-firstname`}
                  value={person.firstName || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'firstName', e.target.value)}
                  className={!person.firstName ? 'field-error' : ''}
                  required
                />
                {attemptedSubmit && !person.firstName && <div className="error-message">First Name is required</div>}
              </div>
              
              <div className="form-group required">
                <label htmlFor={`person-${idx}-lastname`}>Last Name:</label>
                <input 
                  type="text"
                  id={`person-${idx}-lastname`}
                  value={person.lastName || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'lastName', e.target.value)}
                  className={!person.lastName ? 'field-error' : ''}
                  required
                />
                {attemptedSubmit && !person.lastName && <div className="error-message">Last Name is required</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor={`person-${idx}-suffix`}>Suffix:</label>
                <input 
                  type="text"
                  id={`person-${idx}-suffix`}
                  value={person.suffix || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'suffix', e.target.value)}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group required">
                <label htmlFor={`person-${idx}-role`}>Project Role:</label>
                <input 
                  type="text"
                  id={`person-${idx}-role`}
                  value={person.projectRole || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'projectRole', e.target.value)}
                  className={!person.projectRole ? 'field-error' : ''}
                  required
                />
                {attemptedSubmit && !person.projectRole && <div className="error-message">Project Role is required</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor={`person-${idx}-base`}>Base Salary ($):</label>
                <input 
                  type="number"
                  id={`person-${idx}-base`}
                  value={person.baseSalary || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'baseSalary', e.target.value)}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`person-${idx}-calendar`}>Calendar Months:</label>
                <input 
                  type="number"
                  id={`person-${idx}-calendar`}
                  value={person.calendarMonths || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'calendarMonths', e.target.value)}
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor={`person-${idx}-academic`}>Academic Months:</label>
                <input 
                  type="number"
                  id={`person-${idx}-academic`}
                  value={person.academicMonths || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'academicMonths', e.target.value)}
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor={`person-${idx}-summer`}>Summer Months:</label>
                <input 
                  type="number"
                  id={`person-${idx}-summer`}
                  value={person.summerMonths || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'summerMonths', e.target.value)}
                  step="0.01"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group required">
                <label htmlFor={`person-${idx}-requested`}>Requested Salary ($):</label>
                <input 
                  type="number"
                  id={`person-${idx}-requested`}
                  value={person.requestedSalary || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'requestedSalary', e.target.value)}
                  className={!person.requestedSalary ? 'field-error' : ''}
                  required
                  step="0.01"
                />
                {attemptedSubmit && !person.requestedSalary && <div className="error-message">Requested Salary is required</div>}
              </div>
              
              <div className="form-group required">
                <label htmlFor={`person-${idx}-fringe`}>Fringe Benefits ($):</label>
                <input 
                  type="number"
                  id={`person-${idx}-fringe`}
                  value={person.fringeBenefits || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'fringeBenefits', e.target.value)}
                  className={!person.fringeBenefits ? 'field-error' : ''}
                  required
                  step="0.01"
                />
                {attemptedSubmit && !person.fringeBenefits && <div className="error-message">Fringe Benefits is required</div>}
              </div>
              
              <div className="form-group required">
                <label htmlFor={`person-${idx}-funds`}>Funds Requested ($):</label>
                <input 
                  type="number"
                  id={`person-${idx}-funds`}
                  value={person.fundsRequested || ''}
                  onChange={(e) => handleInputChange('seniorKeyPersonnel', idx, 'fundsRequested', e.target.value)}
                  className={!person.fundsRequested ? 'field-error' : ''}
                  required
                  step="0.01"
                />
                {attemptedSubmit && !person.fundsRequested && <div className="error-message">Funds Requested is required</div>}
              </div>
            </div>
          </div>
        ))}
        
        <button 
          type="button" 
          className="add-item-btn"
          onClick={() => addSeniorKeyPerson()}
        >
          + Add Senior/Key Person
        </button>
        
        {/* <div className="file-upload-section">
          <h4>Additional Senior/Key Persons</h4>
          <p>If you have additional senior/key personnel, please upload a document containing their details.</p>
          
          <input
            type="file"
            id="additional-personnel-file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.xlsx,.xls"
          />
          
          {seniorKeyPersonnel.additionalFileUrl ? (
            <div className="uploaded-file-info">
              <span className="filename">{seniorKeyPersonnel.additionalFileName}</span>
              <div className="file-actions">
                <button 
                  type="button"
                  className="view-btn"
                  onClick={() => window.open(seniorKeyPersonnel.additionalFileUrl, '_blank')}
                >
                  View
                </button>
                <button 
                  type="button"
                  className="replace-btn"
                  onClick={() => fileInputRef.current.click()}
                  disabled={isSaving}
                >
                  Replace
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="upload-btn"
              onClick={() => fileInputRef.current.click()}
              disabled={isSaving}
            >
              {isSaving ? "Uploading..." : "Upload Additional Personnel"}
            </button>
          )}
        </div> */}
        
        <div className="subtotal">
          <span>Total Funds Requested:</span>
          <span>${seniorKeyPersonnel.subtotal.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  // Add this function to add a senior/key person
  const addSeniorKeyPerson = () => {
    const newPerson = {
      prefix: '',
      firstName: '',
      lastName: '',
      suffix: '',
      projectRole: '',
      baseSalary: '',
      calendarMonths: '',
      academicMonths: '',
      summerMonths: '',
      requestedSalary: '',
      fringeBenefits: '',
      fundsRequested: 0
    };
    
    addItemToCategory('seniorKeyPersonnel', newPerson);
  };

  const validateForm = () => {
    const currentForm = getCurrentBudgetForm();
    
    // Check required fields
    if (!currentForm.uei) {
      setError('UEI Number is required');
      return false;
    }
    
    if (!currentForm.periodStartDate) {
      setError('Start Date is required');
      return false;
    }
    
    if (!currentForm.periodEndDate) {
      setError('End Date is required');
      return false;
    }
    
    // Validate each senior/key person
    if (currentForm.seniorKeyPersonnel && currentForm.seniorKeyPersonnel.items) {
      for (let i = 0; i < currentForm.seniorKeyPersonnel.items.length; i++) {
        const person = currentForm.seniorKeyPersonnel.items[i];
        
        if (!person.firstName) {
          setError(`Person ${i+1}: First Name is required`);
          return false;
        }
        
        if (!person.lastName) {
          setError(`Person ${i+1}: Last Name is required`);
          return false;
        }
        
        if (!person.projectRole) {
          setError(`Person ${i+1}: Project Role is required`);
          return false;
        }
        
        if (!person.requestedSalary) {
          setError(`Person ${i+1}: Requested Salary is required`);
          return false;
        }
        
        if (!person.fringeBenefits) {
          setError(`Person ${i+1}: Fringe Benefits is required`);
          return false;
        }
        
        if (!person.fundsRequested) {
          setError(`Person ${i+1}: Funds Requested is required`);
          return false;
        }
      }
    }

    // Validate other personnel with non-zero funds requested
    if (currentForm.otherPersonnel && currentForm.otherPersonnel.items) {
      for (let i = 0; i < currentForm.otherPersonnel.items.length; i++) {
        const person = currentForm.otherPersonnel.items[i];
        
        // Only validate if fundsRequested is greater than 0
        if (parseFloat(person.fundsRequested || 0) > 0) {
          if (!person.numberOfPersonnel) {
            setError(`${person.type || 'Other Personnel'}: Number of Personnel is required`);
            return false;
          }
          
          if (!person.projectRole) {
            setError(`${person.type || 'Other Personnel'}: Project Role is required`);
            return false;
          }
          
          if (!person.requestedSalary) {
            setError(`${person.type || 'Other Personnel'}: Requested Salary is required`);
            return false;
          }
          
          if (!person.fringeBenefits) {
            setError(`${person.type || 'Other Personnel'}: Fringe Benefits is required`);
            return false;
          }
        }
      }
    }
    
    return true;
  };
  
  // Render the Section B: Other Personnel section
  const renderOtherPersonnelSection = () => {
    const currentForm = getCurrentBudgetForm();
    const otherPersonnel = currentForm.otherPersonnel;

    console.log("Current Other Personnel Data:", otherPersonnel);
    
    return (
      <div className="budget-section">
        <h3>Section B: Other Personnel</h3>
        
        {otherPersonnel.items.map((person, idx) => (
          <div key={idx} className="budget-item">
            <div className="budget-item-header">
              <h4>{person.type}</h4>
              <button 
                type="button" 
                className="remove-item-btn"
                onClick={() => removeItemFromCategory('otherPersonnel', idx)}
              >
                Remove
              </button>
            </div>
            
            <div className="form-row">
              <div className="form-group required">
                <label htmlFor={`other-${idx}-count`}>Number of Personnel:</label>
                <input 
                  type="number"
                  id={`other-${idx}-count`}
                  value={person.numberOfPersonnel || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'numberOfPersonnel', e.target.value)}
                  className={attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.numberOfPersonnel ? 'field-error' : ''}
                  min="0"
                />
                {attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.numberOfPersonnel && (
                  <div className="error-message">Number of Personnel is required</div>
                )}
              </div>
              
              <div className="form-group required">
                <label htmlFor={`other-${idx}-role`}>Project Role:</label>
                <input 
                  type="text"
                  id={`other-${idx}-role`}
                  value={person.projectRole || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'projectRole', e.target.value)}
                  className={attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.projectRole ? 'field-error' : ''}
                />
                {attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.projectRole && (
                  <div className="error-message">Project Role is required</div>
                )}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`other-${idx}-calendar`}>Calendar Months:</label>
                <input 
                  type="number"
                  id={`other-${idx}-calendar`}
                  value={person.calendarMonths || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'calendarMonths', e.target.value)}
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor={`other-${idx}-academic`}>Academic Months:</label>
                <input 
                  type="number"
                  id={`other-${idx}-academic`}
                  value={person.academicMonths || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'academicMonths', e.target.value)}
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor={`other-${idx}-summer`}>Summer Months:</label>
                <input 
                  type="number"
                  id={`other-${idx}-summer`}
                  value={person.summerMonths || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'summerMonths', e.target.value)}
                  step="0.01"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group required">
                <label htmlFor={`other-${idx}-requested`}>Requested Salary ($):</label>
                <input 
                  type="number"
                  id={`other-${idx}-requested`}
                  value={person.requestedSalary || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'requestedSalary', e.target.value)}
                  className={attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.requestedSalary ? 'field-error' : ''}
                  step="0.01"
                />
                {attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.requestedSalary && (
                  <div className="error-message">Requested Salary is required</div>
                )}
              </div>
              
              <div className="form-group required">
                <label htmlFor={`other-${idx}-fringe`}>Fringe Benefits ($):</label>
                <input 
                  type="number"
                  id={`other-${idx}-fringe`}
                  value={person.fringeBenefits || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'fringeBenefits', e.target.value)}
                  className={attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.fringeBenefits ? 'field-error' : ''}
                  step="0.01"
                />
                {attemptedSubmit && parseFloat(person.fundsRequested) > 0 && !person.fringeBenefits && (
                  <div className="error-message">Fringe Benefits is required</div>
                )}
              </div>
              
              <div className="form-group required">
                <label htmlFor={`other-${idx}-funds`}>Funds Requested ($):</label>
                <input 
                  type="number"
                  id={`other-${idx}-funds`}
                  value={person.fundsRequested || ''}
                  onChange={(e) => handleInputChange('otherPersonnel', idx, 'fundsRequested', e.target.value)}
                  step="0.01"
                />
              </div>
            </div>
          </div>
        ))}
        
        <button 
          type="button" 
          className="add-item-btn"
          onClick={addOtherPersonnel}
        >
          + Add Item to Category
        </button>
        
        <div className="subtotal-summary">
          <div className="subtotal">
            <span>Total Number of Other Personnel:</span>
            <span>{otherPersonnel.totalCount}</span>
          </div>
          <div className="subtotal">
            <span>Total Other Personnel Funds Requested:</span>
            <span>${otherPersonnel.subtotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render the Total Personnel Costs section
  const renderTotalPersonnelCostsSection = () => {
    const currentForm = getCurrentBudgetForm();
    const sectionATotal = currentForm.seniorKeyPersonnel?.subtotal || 0;
    const sectionBTotal = currentForm.otherPersonnel?.subtotal || 0;
    const total = currentForm.totalSalaryWagesFringes;
    
    return (
      <div className="budget-section total-section">
        <h3>Total Salary, Wages and Fringe Benefits (Sections A & B)</h3>
        
        <div className="total-summary">
          <div className="total-row">
            <span>Total Section A (Senior/Key Person):</span>
            <span>${sectionATotal.toFixed(2)}</span>
          </div>
          <div className="total-row">
            <span>Total Section B (Other Personnel):</span>
            <span>${sectionBTotal.toFixed(2)}</span>
          </div>
          <div className="total-row grand-total">
            <span>Total Salary, Wages and Fringe Benefits:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  const addOtherPersonnel = () => {
    const newType = {
      type: 'Other Personnel',
      numberOfPersonnel: '',
      projectRole: '',
      calendarMonths: '',
      academicMonths: '',
      summerMonths: '',
      requestedSalary: '',
      fringeBenefits: '',
      fundsRequested: 0
    };
    
    addItemToCategory('otherPersonnel', newType);
  };

  const renderEquipmentSection = () => {
    const currentForm = getCurrentBudgetForm();
    const equipment = currentForm.equipment;
  
    return (
      <div className="budget-section">
        <h3>Section C: Equipment</h3>
        {equipment.items.map((item, idx) => (
          <div key={idx} className="budget-item">
            <div className="form-row">
              <div className="form-group required">
                <label htmlFor={`equipment-${idx}-name`}>Equipment Name:</label>
                <input
                  type="text"
                  id={`equipment-${idx}-name`}
                  value={item.name || ''}
                  onChange={e => handleInputChange('equipment', idx, 'name', e.target.value)}
                  required
                />
              </div>
              <div className="form-group required">
                <label htmlFor={`equipment-${idx}-funds`}>Funds Requested ($):</label>
                <input
                  type="number"
                  id={`equipment-${idx}-funds`}
                  value={item.fundsRequested || ''}
                  onChange={e => handleInputChange('equipment', idx, 'fundsRequested', e.target.value)}
                  step="0.01"
                  required
                />
              </div>
              <button
                type="button"
                className="remove-item-btn"
                onClick={() => removeItemFromCategory('equipment', idx)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="add-item-btn"
          onClick={addEquipmentItem}
        >
          + Add Equipment
        </button>
        <div className="subtotal">
          <span>Total Equipment Funds Requested:</span>
          <span>${equipment.subtotal.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const addEquipmentItem = () => {
    const newItem = { name: '', fundsRequested: '' };
    addItemToCategory('equipment', newItem);
  };

  // Excel file upload handler
  const handleExcelFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload an Excel file (.xlsx, .xls) or CSV file.');
      return;
    }
    
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }
    
    try {
      // setIsSaving(true);
      setError(null);
      
      // Get upload URL
      const getUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: 'budget-excel-file',
          filename: file.name,
          contentType: file.type,
        })
      });

      if (!getUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const urlData = await getUrlResponse.json();
      
      // Upload file to Cloud Storage
      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      // Confirm upload
      const confirmResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: 'budget-excel-file',
          filename: file.name,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl
        })
      });

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm file upload');
      }
      
      // Update state
      setExcelFile(file);
      setUploadedFileName(file.name);
      setUploadedFileUrl(urlData.downloadUrl);
      
      console.log('File uploaded successfully:', {
        fileName: file.name,
        downloadUrl: urlData.downloadUrl
      });
      
    } catch (error) {
      console.error('Error uploading Excel file:', error);
      setError('Failed to upload Excel file: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Render Excel upload section
  const renderExcelUploadSection = () => {
    return (
      <div className="budget-section excel-upload-section">
        <h3>Budget Upload</h3>
        <p className="section-description">
          Please upload your complete budget information using our Excel template. 
          The template includes all required budget categories and calculations.
        </p>
        
        <div className="template-download">
          <h4>Step 1: Download Template</h4>
          <button 
            type="button" 
            className="template-download-btn"
            onClick={() => window.open('/budget-template.xlsx', '_blank')}
          >
            Download Budget Template
          </button>
          <p className="template-note">
            Download the template, fill in your budget information, and upload the completed file.
          </p>
        </div>
        
        <div className="file-upload">
          <h4>Step 2: Upload Completed Budget</h4>
          <input
            type="file"
            id="excel-budget-file"
            ref={fileInputRef}
            onChange={handleExcelFileChange}
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
          />
          
          {/* Debug info */}
          {console.log('Render check - uploadedFileUrl:', uploadedFileUrl, 'uploadedFileName:', uploadedFileName)}
          {uploadedFileUrl ? (
            <div className="uploaded-file-info">
              <div className="file-info">
                <span className="file-icon">📄</span>
                <span className="filename">{uploadedFileName}</span>
                <span className="file-status success">✅ Uploaded</span>
              </div>
              <div className="file-actions">
                <button 
                  type="button"
                  className="view-btn"
                  onClick={() => window.open(uploadedFileUrl, '_blank')}
                >
                  View File
                </button>
                <button 
                  type="button"
                  className="replace-btn"
                  onClick={() => fileInputRef.current.click()}
                  disabled={isSaving}
                >
                  Replace File
                </button>
              </div>
            </div>
          ) : (
            <div className="upload-area">
              <button
                type="button"
                className="upload-btn"
                onClick={() => fileInputRef.current.click()}
                disabled={isSaving}
              >
                {isSaving ? "Uploading..." : "Upload Budget File"}
              </button>
              <p className="upload-note">
                Accepted formats: Excel (.xlsx, .xls) or CSV (.csv) files up to 10MB
              </p>
            </div>
          )}
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    );
  };

  
  if (isLoading) {
    return (
      <div className="budget-forms-container">
        <div className="loading">Loading application data...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="budget-forms-container">
        <div className="error-message">{error}</div>
        <button className="return-btn" onClick={handleReturn}>
          Return to Grant Applications
        </button>
      </div>
    );
  }
  
  return (
    <div className="budget-forms-container">
      <div className="budget-header">
        <h1>Budget Forms</h1>
        <button className="return-btn" onClick={handleReturn}>
          Return to Grant Applications
        </button>
      </div>
      
      <div className="application-info-bar">
        <h2>{application?.name}</h2>
      </div>
      
      {saveSuccess && (
        <div className="save-success-message">
          Budget data saved successfully!
        </div>
      )}
      
      {/* COMMENTED OUT - Period and Type selectors 
      <div className="budget-controls">
        <div className="period-selector">
          <h3>Budget Period:</h3>
          <div className="period-tabs">
            {budgetData && Object.keys(budgetData).map((period) => (
              <button
                key={period}
                className={`period-tab ${parseInt(period) === activePeriod ? 'active' : ''}`}
                onClick={() => setActivePeriod(parseInt(period))}
              >
                Period {period}
              </button>
            ))}
            <button className="add-period-btn" onClick={handleAddPeriod}>
              + Add Period
            </button>
          </div>
        </div>
        
        <div className="budget-type-selector">
          <h3>Budget Type:</h3>
          <div className="type-tabs">
            <button
              className={`type-tab ${activeType === 'project' ? 'active' : ''}`}
              onClick={() => setActiveType('project')}
            >
              Project
            </button>
            <button
              className={`type-tab ${activeType === 'subaward' ? 'active' : ''}`}
              onClick={() => setActiveType('subaward')}
            >
              Subaward/Consortium
            </button>
          </div>
        </div>
      </div>
      */}
      
      <div className="budget-form">
        {/* COMMENTED OUT - Original budget form sections */}
        {/* 
        {renderBasicInfoSection()}
        {renderSeniorKeyPersonnelSection()}
        {renderOtherPersonnelSection()}
        {renderTotalPersonnelCostsSection()}
        {renderEquipmentSection()}
        {renderTravelSection()}
        {renderParticipantSupportSection()}
        {renderOtherDirectCostsSection()}
        {renderDirectCostsSection()}
        {renderIndirectCostsSection()}
        {renderTotalDirectAndIndirectSection()}
        {renderFeeSection()}
        {renderTotalCostsAndFeeSection()}
        */}
        
        {/* Excel File Upload Section */}
        {renderExcelUploadSection()}
        
        <div className="form-actions">
          <button
            type="button"
            className="save-btn"
            onClick={handleSaveBudget}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Budget'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetForms;